// Heroes.tsx — Four editorial hero components + HeroZone

import { useState } from "react";
import type { HeroMetric, PlanHealthData, KnowledgeHealthData, AttentionQueueData, SystemPulseData, NavigationIntent, ProposalDetail } from "../api/types.js";
import { TrendSparkline, classifyTrendState } from "./TrendSparkline.js";
import type { TrendSeries } from "./TrendSparkline.js";

// ─── Color palettes ────────────────────────────────────────────

const V4_PAL: Record<
  string,
  { tint: string; edge: string; eyebrow: string; metric: string }
> = {
  alert: {
    tint: "var(--danger-soft)",
    edge: "var(--danger)",
    eyebrow: "var(--danger)",
    metric: "var(--danger)",
  },
  attention: {
    tint: "var(--warn-soft)",
    edge: "var(--warn)",
    eyebrow: "var(--warn)",
    metric: "var(--warn)",
  },
  watch: {
    tint: "var(--accent-soft)",
    edge: "var(--accent)",
    eyebrow: "var(--accent)",
    metric: "var(--accent)",
  },
  calm: {
    tint: "var(--ok-soft)",
    edge: "var(--ok)",
    eyebrow: "var(--ok)",
    metric: "var(--ok)",
  },
};

// ─── HeroZone (shared template) ────────────────────────────────────

interface HeroBase {
  severity: string;
  eyebrow: string;
  headline: string;
  because: string;
  metric: HeroMetric;
}

export function HeroZone({
  hero,
  children,
}: {
  hero: HeroBase;
  children?: React.ReactNode;
}) {
  const pal = V4_PAL[hero.severity] ?? V4_PAL.calm;
  return (
    <div
      style={{
        position: "relative",
        background: pal.tint,
        borderBottom: `1px solid ${pal.edge}`,
        padding: "28px 22px 26px",
      }}
    >
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          fontWeight: 700,
          color: pal.eyebrow,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          marginBottom: 16,
        }}
      >
        {hero.eyebrow}
      </div>

      <div
        style={{
          fontSize: 30,
          lineHeight: 1.15,
          fontWeight: 700,
          color: "var(--text)",
          letterSpacing: "-0.025em",
          marginBottom: 14,
        }}
      >
        {hero.headline}
      </div>

      <div
        style={{
          fontSize: 14,
          lineHeight: 1.55,
          color: "var(--text-muted)",
          marginBottom: 22,
          display: "flex",
          gap: 8,
        }}
      >
        <span
          style={{ color: pal.metric, fontWeight: 700, flexShrink: 0 }}
        >
          ∵
        </span>
        <span style={{ fontStyle: "italic" }}>{hero.because}</span>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 10,
        }}
      >
        <span
          style={{
            fontSize: 52,
            fontWeight: 700,
            color: pal.metric,
            letterSpacing: "-0.04em",
            lineHeight: 1,
            fontFamily: "var(--font-mono)",
          }}
        >
          {hero.metric.value}
        </span>
        <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
          {hero.metric.unit}
        </span>
        <span style={{ flex: 1 }} />
        <span
          style={{
            fontSize: 11,
            color: "var(--text-dim)",
            fontFamily: "var(--font-mono)",
            textAlign: "right",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          {hero.metric.deltaLabel}
        </span>
      </div>

      {children}
    </div>
  );
}

// ─── HeroSparkline ──────────────────────────────────────────
// Reusable sparkline with label and data-state indicator.
// Three visual states via TrendSparkline: no_data, insufficient, real.

export function HeroSparkline({
  label,
  series,
  color = "var(--text)",
  height = 32,
}: {
  label: string;
  series: TrendSeries | null | undefined;
  color?: string;
  height?: number;
}) {
  const state = classifyTrendState(series);
  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 4,
        }}
      >
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 9,
            fontWeight: 700,
            color: "var(--text-dim)",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
          }}
        >
          {label}
        </span>
        {state !== "real" && (
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 9,
              color: "var(--text-dim)",
            }}
          >
            {state === "no_data" ? "NO DATA" : "Insufficient history"}
          </span>
        )}
      </div>
      <TrendSparkline series={series} color={color} height={height} />
    </div>
  );
}

// ─── PlanHealthHero ──────────────────────────────────────────

export function PlanHealthHero({ data: dataProp, onNavigate: _onNavigate }: { data?: PlanHealthData; onNavigate?: (intent: NavigationIntent) => void }) {
  if (!dataProp) {
    return (
      <div className="quiet-state">
        <div className="glyph">{"✓"}</div>
        <h3>Velocity data building</h3>
        <p>Check back after next dispatch</p>
      </div>
    );
  }
  const data = dataProp;
  const pal = V4_PAL[data.severity] ?? V4_PAL.calm;
  const velocity = data.velocity ?? { label: "Velocity", series: null, now: 0, prev: 0 };
  return (
    <>
      <HeroZone hero={data}>
        <div
          style={{
            marginTop: 16,
            paddingTop: 14,
            borderTop: `1px solid ${pal.edge}`,
          }}
        >
          {velocity.now !== 0 || velocity.prev !== 0 ? (
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                marginBottom: 6,
              }}
            >
              <span style={{ flex: 1 }} />
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  color: "var(--text)",
                  fontWeight: 700,
                }}
              >
                {velocity.now}
              </span>
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  color:
                    velocity.now < velocity.prev
                      ? "var(--warn)"
                      : "var(--ok)",
                  marginLeft: 4,
                }}
              >
                {velocity.now < velocity.prev ? "↓" : "↑"}
                {Math.abs(
                  Math.round(
                    ((velocity.now - velocity.prev) /
                      (velocity.prev || 1)) *
                      100,
                  ),
                )}
                %
              </span>
            </div>
          ) : null}
          <HeroSparkline
            label={velocity.label}
            series={velocity.series}
            color={pal.metric}
            height={32}
          />
        </div>
      </HeroZone>

      {/* Signals */}
      <div className="section-head">
        <h2>Signals</h2>
      </div>
      <ul
        style={{
          listStyle: "none",
          margin: 0,
          padding: "0 20px 20px",
          fontSize: 12,
          color: "var(--text-muted)",
          lineHeight: 1.5,
        }}
      >
        {(data.signals ?? []).map((s, i) => (
          <li
            key={i}
            style={{ display: "flex", gap: 8, padding: "4px 0" }}
          >
            <span
              style={{
                fontFamily: "var(--font-mono)",
                color: "var(--text-dim)",
                width: 16,
                flexShrink: 0,
              }}
            >
              0{i + 1}
            </span>
            <span style={{ flex: 1 }}>{s}</span>
          </li>
        ))}
      </ul>
    </>
  );
}

// ─── KnowledgeHealthHero ─────────────────────────────────────────

export function KnowledgeHealthHero({ data: dataProp, onNavigate }: { data?: KnowledgeHealthData; onNavigate?: (intent: NavigationIntent) => void }) {
  if (!dataProp) {
    return (
      <div className="quiet-state">
        <div className="glyph">{"📚"}</div>
        <h3>Entity corpus initializing</h3>
        <p>Data arrives with first sync</p>
      </div>
    );
  }
  const data = dataProp;

  return (
    <>
      <HeroZone hero={data} />

      {/* Drift */}
      <div className="section-head">
        <h2 style={{ color: "var(--warn)" }}>Drift detected</h2>
        <span className="count">{(data.drift ?? []).length}</span>
      </div>
      <div style={{ padding: "0 20px 8px" }}>
        {(data.drift ?? []).map((d) => (
          <div
            key={d.id}
            style={{
              background: "var(--warn-soft)",
              border: "1px solid var(--warn)",
              borderRadius: 8,
              padding: "12px 14px",
              marginBottom: 8,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 8,
                marginBottom: 4,
              }}
            >
              <span
                className="mono"
                style={{
                  color: "var(--warn)",
                  fontSize: 10,
                  fontWeight: 700,
                }}
              >
                {d.id}
              </span>
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "var(--text)",
                marginBottom: 6,
              }}
            >
              {d.title}
            </div>
            <div
              style={{
                fontSize: 11.5,
                color: "var(--text-muted)",
                lineHeight: 1.5,
                display: "flex",
                gap: 6,
                marginBottom: 10,
                fontStyle: "italic",
              }}
            >
              <span
                style={{
                  color: "var(--warn)",
                  fontWeight: 700,
                  fontStyle: "normal",
                }}
              >
                ∵
              </span>
              <span>{d.why}</span>
            </div>
            <button
              onClick={() => onNavigate?.({ tab: "entities", subpath: d.id })}
              style={{
                padding: "7px 12px",
                background: "var(--warn)",
                color: "var(--bg)",
                border: "none",
                borderRadius: 6,
                fontSize: 11,
                fontWeight: 700,
                fontFamily: "inherit",
                cursor: "pointer",
              }}
            >
              {d.suggestedAction} →
            </button>
          </div>
        ))}
      </div>

    </>
  );
}

// ─── ProposalActionItems ─────────────────────────────────────────

function ProposalActionItems({
  proposals,
  onReload,
}: {
  proposals: ProposalDetail[];
  onReload?: () => void;
}) {
  const [actionInFlight, setActionInFlight] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  async function handleAction(id: string, action: "adopt" | "reject") {
    setActionInFlight(id + ":" + action);
    setActionError(null);
    try {
      const res = await fetch("/api/pwa/ratify-proposal", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ proposal_id: id, action }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText })) as { error?: string };
        throw new Error(err.error ?? "ratify failed");
      }
      onReload?.();
    } catch (e) {
      setActionError(String(e));
    } finally {
      setActionInFlight(null);
    }
  }

  if (proposals.length === 0) return null;

  return (
    <>
      <div className="section-head">
        <h2>Proposals</h2>
        <span className="count">{proposals.length}</span>
      </div>
      {actionError && (
        <div style={{ padding: "8px 20px", color: "var(--danger)", fontFamily: "var(--font-mono)", fontSize: 11 }}>
          {actionError}
        </div>
      )}
      {proposals.map((p) => (
        <div key={p.id} className="card" style={{ margin: "0 0 1px", borderRadius: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <span className="mono" style={{ color: "var(--accent)", fontSize: 10 }}>{p.id}</span>
            {p.confidence && <span className="chip">{p.confidence}</span>}
            <span className="chip" style={{ marginLeft: "auto" }}>
              {p.evidence_count} evidence · {p.candidate_steps_count} steps
            </span>
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.4, marginBottom: 8 }}>{p.north_star}</div>
          {p.candidate_steps.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              {p.candidate_steps.map((s, i) => (
                <div key={i} style={{ fontSize: 11, color: "var(--text-dim)", fontFamily: "var(--font-mono)", padding: "2px 0" }}>
                  {i + 1}. {s.title}
                </div>
              ))}
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="chip ok"
              style={{ cursor: "pointer" }}
              disabled={actionInFlight !== null}
              onClick={() => void handleAction(p.id, "adopt")}
            >
              {actionInFlight === p.id + ":adopt" ? "Adopting…" : "Adopt"}
            </button>
            <button
              className="chip danger"
              style={{ cursor: "pointer" }}
              disabled={actionInFlight !== null}
              onClick={() => void handleAction(p.id, "reject")}
            >
              {actionInFlight === p.id + ":reject" ? "Rejecting…" : "Reject"}
            </button>
          </div>
        </div>
      ))}
    </>
  );
}

// ─── AttentionQueueHero ──────────────────────────────────────────

export function AttentionQueueHero({
  data: dataProp,
  onNavigate,
  onReload,
}: {
  data?: AttentionQueueData;
  onNavigate?: (intent: NavigationIntent) => void;
  onReload?: () => void;
}) {
  if (!dataProp) {
    return (
      <div className="quiet-state">
        <div className="glyph">{"✓"}</div>
        <h3>No blockers</h3>
        <p>Execution unimpeded</p>
      </div>
    );
  }
  const data = dataProp;
  const groups = [
    {
      key: "urgent",
      label: "Urgent · respond now",
      items: (data.items ?? []).filter((i) => i.urgency === "urgent"),
      color: "var(--danger)",
    },
    {
      key: "watch",
      label: "Watch · low SLA",
      items: (data.items ?? []).filter((i) => i.urgency === "watch"),
      color: "var(--accent)",
    },
  ];

  const kindGlyph: Record<string, string> = {
    decision: "◇",
    mention: "@",
    alert: "!",
  };
  const kindColor: Record<string, string> = {
    decision: "var(--accent)",
    mention: "var(--info)",
    alert: "var(--danger)",
  };

  return (
    <>
      <HeroZone hero={data} />

      {groups.map(
        (g) =>
          g.items.length > 0 && (
            <div key={g.key}>
              <div className="section-head">
                <h2 style={{ color: g.color }}>{g.label}</h2>
                <span className="count">{g.items.length}</span>
              </div>
              <div style={{ padding: "0 20px" }}>
                {g.items.map((it) => (
                  <button
                    key={it.id}
                    onClick={() => onNavigate?.({ tab: "entities", subpath: it.kind })}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      background: "transparent",
                      border: "none",
                      borderBottom: "1px solid var(--border-soft)",
                      padding: "12px 0",
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "20px 1fr auto",
                        gap: 10,
                        alignItems: "baseline",
                        marginBottom: 4,
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 14,
                          color: kindColor[it.kind] ?? "var(--text)",
                          fontWeight: 700,
                        }}
                      >
                        {kindGlyph[it.kind] ?? "·"}
                      </span>
                      <div
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          fontFamily: "var(--font-mono)",
                          color: "var(--text-dim)",
                          letterSpacing: "0.1em",
                          textTransform: "uppercase",
                        }}
                      >
                        {it.kind} · {it.id}
                      </div>
                      <span
                        className="mono"
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color:
                            it.urgency === "urgent"
                              ? "var(--danger)"
                              : "var(--text-dim)",
                        }}
                      >
                        {it.deadline}
                      </span>
                    </div>
                    <div style={{ paddingLeft: 30 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "var(--text)",
                          lineHeight: 1.35,
                          marginBottom: 4,
                        }}
                      >
                        {it.title}
                      </div>
                      <div
                        style={{
                          fontSize: 11.5,
                          color: "var(--text-muted)",
                          lineHeight: 1.5,
                          display: "flex",
                          gap: 6,
                          fontStyle: "italic",
                        }}
                      >
                        <span
                          style={{
                            color:
                              kindColor[it.kind] ?? "var(--text)",
                            fontWeight: 700,
                            fontStyle: "normal",
                          }}
                        >
                          ∵
                        </span>
                        <span>{it.because}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ),
      )}

      {/* Signals strip */}
      <div style={{ padding: "14px 20px 24px" }}>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 9,
            fontWeight: 700,
            color: "var(--text-dim)",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            marginBottom: 8,
          }}
        >
          Queue health
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          {(data.signals ?? []).map((s, i) => (
            <div
              key={i}
              style={{
                fontSize: 11,
                color: "var(--text-muted)",
                fontFamily: "var(--font-mono)",
              }}
            >
              · {s}
            </div>
          ))}
        </div>
      </div>
      <ProposalActionItems proposals={data.proposals ?? []} onReload={onReload} />
    </>
  );
}

// ─── SystemPulseHero ─────────────────────────────────────────────

export function SystemPulseHero({ data: dataProp }: { data?: SystemPulseData }) {
  if (!dataProp) {
    return (
      <div className="quiet-state">
        <div className="glyph">{"—"}</div>
        <h3>System idle</h3>
        <p>No dispatches in observation window</p>
      </div>
    );
  }
  const data = dataProp;
  const pal = V4_PAL[data.severity] ?? V4_PAL.calm;
  const hourly = data.hourly ?? [];
  const max = Math.max(...hourly, 1);
  const peakIdx = hourly.indexOf(Math.max(...hourly));
  const toneColor: Record<string, string> = {
    ok: "var(--ok)",
    warn: "var(--warn)",
    danger: "var(--danger)",
    default: "var(--text)",
  };

  return (
    <>
      <HeroZone hero={data}>
        <div
          style={{
            marginTop: 16,
            paddingTop: 14,
            borderTop: `1px solid ${pal.edge}`,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              marginBottom: 8,
            }}
          >
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 9,
                fontWeight: 700,
                color: "var(--text-dim)",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
              }}
            >
              Events / hour · last 24h
            </span>
            <span style={{ flex: 1 }} />
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                color: "var(--text-dim)",
              }}
            >
              peak {data.peakHour}
            </span>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(24, 1fr)",
              gap: 2,
              alignItems: "end",
              height: 40,
            }}
          >
            {hourly.map((v, i) => {
              const pct = v === 0 ? 4 : Math.max(8, (v / max) * 100);
              const isPeak = i === peakIdx;
              return (
                <div
                  key={i}
                  style={{
                    height: `${pct}%`,
                    background: isPeak
                      ? pal.metric
                      : v === 0
                        ? "var(--border-soft)"
                        : pal.edge,
                    borderRadius: 1,
                    opacity: v === 0 ? 0.5 : 1,
                  }}
                />
              );
            })}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 4,
              fontFamily: "var(--font-mono)",
              fontSize: 9,
              color: "var(--text-dim)",
            }}
          >
            {[0, 6, 12, 18, 23].map((pos) => {
              const h = data.currentHour != null ? (data.currentHour + pos) % 24 : pos;
              return <span key={pos}>{String(h).padStart(2, "0")}</span>;
            })}
          </div>
          <div
            style={{
              marginTop: 6,
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              color: "var(--text-dim)",
            }}
          >
            quiet · {data.quietRange}
          </div>
        </div>
      </HeroZone>

      {/* Digest grid */}
      <div className="section-head">
        <h2>Last 24h</h2>
        <span className="count">rolling window</span>
      </div>
      <div
        style={{
          padding: "0 20px 8px",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 8,
        }}
      >
        {(data.digest ?? []).map((d) => (
          <div
            key={d.label}
            style={{
              background: "var(--bg-elev)",
              border: "1px solid var(--border-soft)",
              borderRadius: 8,
              padding: "10px 12px",
            }}
          >
            <div
              style={{
                fontSize: 9,
                color: "var(--text-dim)",
                fontFamily: "var(--font-mono)",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                marginBottom: 4,
              }}
            >
              {d.label}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 6,
              }}
            >
              <span
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  color: toneColor[d.tone] ?? "var(--text)",
                  fontFamily: "var(--font-mono)",
                  lineHeight: 1,
                }}
              >
                {d.value}
              </span>
              <span
                style={{
                  fontSize: 10,
                  color: "var(--text-muted)",
                  fontFamily: "var(--font-mono)",
                }}
              >
                {d.delta}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Planner */}
      <div className="section-head">
        <h2>Planner</h2>
      </div>
      <div style={{ padding: "0 20px 8px" }}>
        <div
          style={{
            background: "var(--bg-elev)",
            border: "1px solid var(--border-soft)",
            borderRadius: 10,
            padding: 14,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 9,
                color: "var(--text-dim)",
                fontFamily: "var(--font-mono)",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                marginBottom: 4,
              }}
            >
              Cycles
            </div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: "var(--text)",
                fontFamily: "var(--font-mono)",
              }}
            >
              {data.plannerCycles}
            </div>
          </div>
          <div>
            <div
              style={{
                fontSize: 9,
                color: "var(--text-dim)",
                fontFamily: "var(--font-mono)",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                marginBottom: 4,
              }}
            >
              Holds
            </div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color:
                  data.plannerHolds > 5
                    ? "var(--warn)"
                    : "var(--text)",
                fontFamily: "var(--font-mono)",
              }}
            >
              {data.plannerHolds}
            </div>
          </div>
        </div>
      </div>

    </>
  );
}
